import styled, { css } from 'styled-components';

import { BaseStyle } from '@nhl/nhl-breakaway-ui';

import { WithHidden } from '~/core/styleTypes';
import { StrictWidthContainer } from '~/views/components/strictWidthText/styles';
import { LineBreak, NetworkContainer, NetworkLogos, NetworkNames } from '~/views/components/tvBroadcast/styles';

export const TOP_BAR_BASE_HEIGHT = 22;

export const GameCardContainer = styled.div`
    ${BaseStyle.Mixins.gaps.medium}
    ${BaseStyle.Mixins.utility.flexSizing(1, `${BaseStyle.GAP_SIZES.large}px`)}
    
    padding: 16px;

    position: relative;
    display: flex;
    flex-direction: column;
    max-width: 678px;
    border: 1px solid ${({ theme }) => theme.Surface.border};
    border-radius: 8px;

    ${BaseStyle.Mixins.screenSizes.medium} {
        ${BaseStyle.Mixins.utility.flexSizing(2, `${BaseStyle.GAP_SIZES.large}px`)}
    }

    ${BaseStyle.Mixins.screenSizes.extraLarge} {
        ${BaseStyle.Mixins.utility.flexSizing(3, `${BaseStyle.GAP_SIZES.large}px`)}
    }
`;

export const TopBar = styled.div`
    display: flex;
    flex-direction: column;
    height: 32px;
`;

export const ClockAndBroadcastLogosContainer = styled.div`
    ${BaseStyle.Mixins.gaps.small}
    display: flex;
    height: 32px;
    justify-content: space-between;
`;

export const Situation = styled.div`
    ${BaseStyle.Mixins.fontStyles.ui['14b']}
    ${BaseStyle.Mixins.gaps.small}
    
    display: flex;
    justify-content: right;
    align-items: center;
    height: ${TOP_BAR_BASE_HEIGHT}px;

    > span {
        color: ${({ theme }) => theme.Utility.critical};
    }
`;

export const Broadcasters = styled.div<{ $hasNationalBroadcasts: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['14m']}
    display: flex;
    color: ${({ theme }) => theme.Text.secondary};
    height: ${TOP_BAR_BASE_HEIGHT}px;

    ${NetworkLogos} {
        justify-content: right;
    }

    ${NetworkContainer} {
        align-items: end;
        row-gap: 0;
        height: ${TOP_BAR_BASE_HEIGHT}px;
    }

    ${LineBreak}:last-child {
        display: none;
    }

    ${NetworkNames} {
        width: fit-content;
    }
`;

export const TeamBar = styled.div`
    ${BaseStyle.Mixins.gaps.medium}
    display: flex;
    flex-direction: column;
`;

export const PlayerContainer = styled.div`
    ${BaseStyle.Mixins.gaps.small}
    display: flex;
    flex-direction: column;
`;

export const PlayerCarouselHeader = styled.div`
    ${BaseStyle.Mixins.fontStyles.ui['12b']}
    width: 150px;
`;

export const BottomBar = styled.div`
    ${BaseStyle.Mixins.gaps.small}
    display: flex;
    flex-direction: row;
    justify-content: center;
    align-items: center;
    width: 100%;
    cursor: pointer;
`;

export const LinkContainer = styled.a<WithHidden>`
    ${BaseStyle.Mixins.fontStyles.ui['12b']}
    display: ${({ $isHidden }) => ($isHidden ? 'none' : 'flex')};
    flex-grow: 1;
    flex-basis: 0;
    min-width: 0;
    height: 28px;
    justify-content: center;
    align-items: center;
    border-radius: 8px;
    border: solid 1px ${({ theme }) => theme.Surface.border};
    padding: 0 8px;

    &:hover {
        svg {
            fill: ${({ theme }) => theme.Text.primary};
        }
    }

    && {
        color: ${({ theme }) => theme.Utility.cta};
    }

    svg {
        ${BaseStyle.Mixins.fontStyles.ui['16m']}
        fill: ${({ theme }) => theme.Utility.cta};
        margin-right: 4px;
    }

    ${BaseStyle.Mixins.screenSizes.small} {
        ${BaseStyle.Mixins.fontStyles.ui['10b']}
    }

    ${StrictWidthContainer} {
        text-align: center;
        line-height: 15px;

        color: ${({ theme }) => theme.Utility.cta};

        &:hover {
            color: ${({ theme }) => theme.Text.primary};
        }

        ${BaseStyle.Mixins.screenSizes.small} {
            line-height: 12px;
        }
    }
`;

export const PlayoffHeader = styled.a<{ $hasLink: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['12m']}
    ${BaseStyle.Mixins.gaps.tiny}
    display: flex;
    padding: 8px;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    border-radius: 4px;
    height: 42px;
    background-color: ${({ theme }) => theme.Surface.secondary};
    border: 2px solid transparent;

    ${({ $hasLink }) =>
        $hasLink &&
        css`
            &:hover {
                border-color: ${({ theme }) => theme.Surface.border};
            }

            &:active {
                background-color: ${({ theme }) => theme.Surface.tertiary};
                border-color: transparent;
            }
        `}
`;

export const RoundAndGameContainer = styled.div<{ $hasLink: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['12b']}
    color: ${({ $hasLink, theme }) => ($hasLink ? theme.Utility.cta : theme.Text.primary)}
`;

export const SeriesStatus = styled.div`
    color: ${({ theme }) => theme.Text.secondary};
`;

export const LocalBroadcastContainer = styled.div<{ $hasNationalBroadcasts: boolean }>`
    ${BaseStyle.Mixins.fontStyles.ui['12m']}
    display: flex;
    width: 100%;
    margin-top: ${({ $hasNationalBroadcasts }) => ($hasNationalBroadcasts ? '3px' : '-28px')};
    justify-content: end;
`;
